import React, { useEffect, useMemo, useState } from 'react';
import { Spinner, Modal, Form, Button } from 'react-bootstrap';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import { crearComentario, getComentariosPorSiniestro } from '../../services/art/comentarioSiniestroService';
import { getLesiones } from '../../services/art/lesionService';
import { toast } from 'react-toastify';
import ModalHistorialSiniestro from './ModalHistorialSiniestro';
import '../../style/components/SiniestroHorizontal.css';

const ListarSiniestros = ({
    siniestros = [],
    loading = false,
    onRefresh,
    tramiteEstadoActual
}) => {
    const [historialModalOpen, setHistorialModalOpen] = useState(false);
    const [siniestroSeleccionado, setSiniestroSeleccionado] = useState(null);
    const [modalCalificacionOpen, setModalCalificacionOpen] = useState(false);
    const [calificacionSeleccionada, setCalificacionSeleccionada] = useState(null);
    const [comentarioTexto, setComentarioTexto] = useState('');
    const [etiquetaOtra, setEtiquetaOtra] = useState('');
    const [cargandoCalificacion, setCargandoCalificacion] = useState(false);
    const [siniestroACalificar, setSiniestroACalificar] = useState(null);
    const [lesionesDisponibles, setLesionesDisponibles] = useState([]);
    const [siniestrosConComentarios, setSiniestrosConComentarios] = useState([]);

    const ESTADOS_POST_CITACION = [
        'respuesta art',
        'iniciado en comisión médica',
        'resolución srt',
        'judicializado',
        'finalizado'
    ];

    useEffect(() => {
        const cargarLesiones = async () => {
            try {
                const data = await getLesiones();
                setLesionesDisponibles(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error al cargar lesiones:', error);
            }
        };

        cargarLesiones();
    }, []);

    // Cargar comentarios para cada siniestro
    useEffect(() => {
        const cargarComentarios = async () => {
            if (!siniestros || siniestros.length === 0) {
                setSiniestrosConComentarios([]);
                return;
            }

            const siniestrosActualizados = await Promise.all(
                siniestros.map(async (siniestro) => {
                    try {
                        const comentarios = await getComentariosPorSiniestro(siniestro.idSiniestro);
                        return {
                            ...siniestro,
                            comentarios: Array.isArray(comentarios) ? comentarios : []
                        };
                    } catch (error) {
                        console.error(`Error al cargar comentarios para siniestro ${siniestro.idSiniestro}:`, error);
                        return {
                            ...siniestro,
                            comentarios: []
                        };
                    }
                })
            );
            setSiniestrosConComentarios(siniestrosActualizados);
        };

        cargarComentarios();
    }, [siniestros]);

    const lesionesPorId = useMemo(() => {
        const map = new Map();
        lesionesDisponibles.forEach((lesion) => {
            const id = lesion?.idLesion ?? lesion?.id;
            if (id !== undefined && id !== null) {
                map.set(String(id), lesion);
            }
        });
        return map;
    }, [lesionesDisponibles]);

    const puedeCalificar = tramiteEstadoActual &&
        ESTADOS_POST_CITACION.includes(tramiteEstadoActual.toLowerCase());

    const obtenerUltimaEtiqueta = (siniestro) => {
        if (Array.isArray(siniestro.comentarios) && siniestro.comentarios.length > 0) {
            const comentariosOrdenados = [...siniestro.comentarios].sort((a, b) =>
                new Date(b.fecha) - new Date(a.fecha)
            );
            return comentariosOrdenados[0]?.etiqueta || null;
        }
        return null;
    };

    // Obtener el último comentario con etiqueta APROBADO o RECHAZADO (fase Respuesta ART)
    const obtenerUltimaCalificacion = (siniestro) => {
        if (!Array.isArray(siniestro.comentarios) || siniestro.comentarios.length === 0) {
            return null;
        }

        const calificaciones = siniestro.comentarios.filter(c => {
            const etiqueta = (c?.etiqueta || '').toUpperCase();
            return etiqueta === 'APROBADO' || etiqueta === 'RECHAZADO';
        });

        if (calificaciones.length === 0) return null;

        return [...calificaciones].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
    };

    // Obtener el último comentario con etiqueta FAVORABLE o DESFAVORABLE (fase Resolución SRT)
    const obtenerCalificacionSRT = (siniestro) => {
        if (!Array.isArray(siniestro.comentarios) || siniestro.comentarios.length === 0) {
            return null;
        }

        const calificaciones = siniestro.comentarios.filter(c => {
            const etiqueta = (c?.etiqueta || '').toUpperCase();
            return etiqueta === 'FAVORABLE' || etiqueta === 'DESFAVORABLE';
        });

        if (calificaciones.length === 0) return null;

        return [...calificaciones].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
    };

    const esEstadoResolucionSRT = tramiteEstadoActual?.toLowerCase() === 'resolución srt';

    const obtenerCalificacionActiva = (siniestro) =>
        esEstadoResolucionSRT
            ? obtenerCalificacionSRT(siniestro)
            : obtenerUltimaCalificacion(siniestro);

    const obtenerColorBorde = (siniestro) => {
        const calSRT = obtenerCalificacionSRT(siniestro);
        const calART = obtenerUltimaCalificacion(siniestro);

        const etiquetaSRT = (calSRT?.etiqueta || '').toUpperCase();
        const etiquetaART = (calART?.etiqueta || '').toUpperCase();

        if (etiquetaSRT === 'FAVORABLE') return '#198754';
        if (etiquetaSRT === 'DESFAVORABLE') return '#fd7e14';
        if (etiquetaART === 'APROBADO') return '#198754';
        if (etiquetaART === 'RECHAZADO') return '#dc3545';
        return '#dee2e6';
    };

    const yaTieneCalificacion = (siniestro) => obtenerCalificacionActiva(siniestro) !== null;

    const obtenerCodigoContingencia = (tipo) => {
        switch ((tipo || '').toUpperCase()) {
            case 'AT':
                return 'AT';
            case 'EP':
                return 'EP';
            case 'EPNL':
                return 'EPNL';
            default:
                return '-';
        }
    };

    const obtenerNombresLesion = (s) => {
        // 1) Nuevo formato: nombresLesiones (lista provista por el backend).
        if (Array.isArray(s?.nombresLesiones) && s.nombresLesiones.length > 0) {
            return s.nombresLesiones.filter(Boolean);
        }

        // 2) Nuevo formato: idsLesiones → resolver contra el catálogo cargado.
        if (Array.isArray(s?.idsLesiones) && s.idsLesiones.length > 0) {
            const nombres = s.idsLesiones
                .map((id) => {
                    const l = lesionesPorId.get(String(id));
                    return l?.nombre || l?.descripcion || null;
                })
                .filter(Boolean);
            if (nombres.length > 0) return nombres;
        }

        // 3) Legacy: un único nombre/idLesion.
        const idLesion = s?.idLesion ?? s?.lesion?.idLesion ?? s?.lesion?.id ?? s?.id_lesion;
        const lesionCatalogo = idLesion !== undefined && idLesion !== null
            ? lesionesPorId.get(String(idLesion))
            : null;

        const nombreUnico = (
            s?.nombreLesion ||
            s?.lesion?.nombre ||
            s?.lesion?.descripcion ||
            lesionCatalogo?.nombre ||
            lesionCatalogo?.descripcion ||
            s?.lesionDescripcion
        );

        return nombreUnico ? [nombreUnico] : [];
    };

    const obtenerNroSiniestro = (s) => s?.nroSiniestro ?? s?.numeroSiniestro ?? s?.nro_siniestro ?? '-';
    const obtenerNroExpediente = (s) => s?.nroExpedienteSRT ?? s?.nro_expediente_srt ?? '-';

    const siniestrosOrdenados = useMemo(() => {
        return [...siniestrosConComentarios].sort((a, b) => {
            const aExp = (obtenerNroExpediente(a) || '').toString();
            const bExp = (obtenerNroExpediente(b) || '').toString();
            return aExp.localeCompare(bExp, 'es', { numeric: true, sensitivity: 'base' });
        });
    }, [siniestrosConComentarios]);

    const abrirHistorial = (siniestro) => {
        setSiniestroSeleccionado(siniestro);
        setHistorialModalOpen(true);
    };

    const handleCerrarHistorial = () => {
        setHistorialModalOpen(false);
        setSiniestroSeleccionado(null);
    };

    const abrirModalCalificacion = (siniestro, tipo) => {
        setSiniestroACalificar(siniestro);
        setCalificacionSeleccionada(tipo);
        setComentarioTexto('');
        setEtiquetaOtra('');
        setModalCalificacionOpen(true);
    };

    const cerrarModalCalificacion = () => {
        setModalCalificacionOpen(false);
        setSiniestroACalificar(null);
        setCalificacionSeleccionada(null);
        setComentarioTexto('');
        setEtiquetaOtra('');
    };

    const handleGuardarCalificacion = async () => {
        if (!siniestroACalificar) return;

        let etiquetaFinal = calificacionSeleccionada;

        if (calificacionSeleccionada === 'OTRO') {
            if (!etiquetaOtra.trim()) {
                toast.error('Debe ingresar una etiqueta personalizada');
                return;
            }
            etiquetaFinal = etiquetaOtra.trim();
        }

        if (!comentarioTexto.trim()) {
            toast.error('Debe ingresar un comentario');
            return;
        }

        setCargandoCalificacion(true);
        try {
            await crearComentario({
                idSiniestro: siniestroACalificar.idSiniestro,
                etiqueta: etiquetaFinal,
                comentario: comentarioTexto.trim()
            });

            toast.success(`Siniestro calificado como ${etiquetaFinal}`);
            cerrarModalCalificacion();
            onRefresh?.();
        } catch (error) {
            console.error('Error al guardar calificación:', error);
            toast.error('Error al guardar la calificación: ' + (error.response?.data?.message || error.message));
        } finally {
            setCargandoCalificacion(false);
        }
    };


    if (!siniestros || siniestros.length === 0) {
        return (
            <div className="alert alert-info border-0 rounded-3 text-center">
                <p className="mb-0">No hay siniestros registrados para este trámite</p>
            </div>
        );
    }

    return (
        <div className="siniestros-container">
            <div className="siniestros-scroll-container">
                <div className="siniestros-list">
                    {siniestrosOrdenados.map((s) => {
                        const colorBorde = obtenerColorBorde(s);
                        const calificado = yaTieneCalificacion(s);
                        const calificacion = obtenerCalificacionActiva(s);
                        const etiquetaActiva = (calificacion?.etiqueta || '').toUpperCase();
                        const esAprobado = etiquetaActiva === 'APROBADO';
                        const esRechazado = etiquetaActiva === 'RECHAZADO';
                        const esFavorable = etiquetaActiva === 'FAVORABLE';
                        const esDesfavorable = etiquetaActiva === 'DESFAVORABLE';
                        const nombresLesionList = obtenerNombresLesion(s);
                        const lesionTitle = nombresLesionList.length > 0 ? nombresLesionList.join(', ') : '-';
                        const nroSiniestro = obtenerNroSiniestro(s);
                        const nroExpediente = obtenerNroExpediente(s);
                        const contingencia = obtenerCodigoContingencia(s?.tipoContingencia ?? s?.tipo_contingencia);

                        return (
                            <div
                                key={s.idSiniestro ?? `${nroExpediente}-${nroSiniestro}`}
                                className="siniestro-card-horizontal"
                                style={{
                                    borderLeftColor: colorBorde,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                onClick={() => abrirHistorial(s)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                }}
                            >
                                <div className="siniestro-info-inline siniestro-info-grid-4">
                                    <div className="info-cell">
                                        <span className="label">EXPEDIENTE SRT N°</span>
                                        <span className="value">{nroExpediente || '-'}</span>
                                    </div>

                                    <div className="info-cell primary">
                                        <span className="label">Nro. Siniestro</span>
                                        <span className="value">#{nroSiniestro}</span>
                                    </div>

                                    <div className="info-cell">
                                        <span className="label">Lesiones</span>
                                        <span className="value" title={lesionTitle}>
                                            {nombresLesionList.length === 0
                                                ? '-'
                                                : nombresLesionList.map((nombre, idx) => (
                                                    <div key={idx}>{nombre}</div>
                                                ))}
                                        </span>
                                    </div>

                                    <div className="info-cell">
                                        <span className="label">Contingencia</span>
                                        <span className="value">{contingencia}</span>
                                    </div>
                                </div>

                                <div className="siniestro-actions-compact">
                                    {calificado ? (
                                        // Badge de estado
                                        <div className="estado-calificacion" style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            padding: '0.5rem 1rem',
                                            borderRadius: '0.375rem',
                                            backgroundColor: esAprobado || esFavorable ? '#d1e7dd'
                                                : esRechazado ? '#f8d7da'
                                                : esDesfavorable ? '#ffe8cc'
                                                : '#e2e8f0',
                                            textAlign: 'center'
                                        }}>
                                            <span style={{
                                                fontWeight: '700',
                                                fontSize: '0.9rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                color: esAprobado || esFavorable ? '#0a3622'
                                                    : esRechazado ? '#842029'
                                                    : esDesfavorable ? '#7c3c00'
                                                    : '#334155'
                                            }}>
                                                {calificacion?.etiqueta || 'SIN ESTADO'}
                                            </span>
                                        </div>
                                    ) : (
                                        puedeCalificar && (
                                            // Botones adaptados al estado del trámite
                                            <div className="action-buttons">
                                                {esEstadoResolucionSRT ? (
                                                    <>
                                                        <button
                                                            className="btn-action btn-aprobado"
                                                            onClick={(e) => { e.stopPropagation(); abrirModalCalificacion(s, 'FAVORABLE'); }}
                                                            title="Resolución favorable (SRT)"
                                                        >
                                                            <CheckCircleIcon fontSize="small" className="me-1" />
                                                            Favorable
                                                        </button>
                                                        <button
                                                            className="btn-action btn-rechazado"
                                                            onClick={(e) => { e.stopPropagation(); abrirModalCalificacion(s, 'DESFAVORABLE'); }}
                                                            title="Resolución desfavorable (SRT)"
                                                        >
                                                            <CancelIcon fontSize="small" className="me-1" />
                                                            Desfavorable
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="btn-action btn-aprobado"
                                                            onClick={(e) => { e.stopPropagation(); abrirModalCalificacion(s, 'APROBADO'); }}
                                                            title="Aprobar siniestro"
                                                        >
                                                            <CheckCircleIcon fontSize="small" className="me-1" />
                                                            Aprobado
                                                        </button>
                                                        <button
                                                            className="btn-action btn-rechazado"
                                                            onClick={(e) => { e.stopPropagation(); abrirModalCalificacion(s, 'RECHAZADO'); }}
                                                            title="Rechazar siniestro"
                                                        >
                                                            <CancelIcon fontSize="small" className="me-1" />
                                                            Rechazado
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    className="btn-action btn-otro"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        abrirModalCalificacion(s, 'OTRO');
                                                    }}
                                                    title="Otra calificación"
                                                >
                                                    <HelpIcon fontSize="small" className="me-1" />
                                                    Otro
                                                </button>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <ModalHistorialSiniestro
                isOpen={historialModalOpen}
                onClose={handleCerrarHistorial}
                siniestroId={siniestroSeleccionado?.idSiniestro}
                numeroSiniestro={obtenerNroSiniestro(siniestroSeleccionado || {})}
                siniestroData={siniestroSeleccionado}
                onComentarioAdded={onRefresh}
                onSiniestroActualizado={onRefresh}
                onSiniestroEliminado={onRefresh}
            />

            <Modal
                show={modalCalificacionOpen}
                onHide={cerrarModalCalificacion}
                centered
                size="lg"
                dialogClassName="modal-calificacion-siniestro-xl"
                contentClassName="modal-calificacion-siniestro-content"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Calificar Siniestro #{obtenerNroSiniestro(siniestroACalificar || {})}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 py-3">
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Tipo de Calificación</label>
                        <div className="alert alert-light border rounded-3 p-3">
                            {calificacionSeleccionada === 'APROBADO' && (
                                <div className="d-flex align-items-center gap-2">
                                    <CheckCircleIcon className="text-success" style={{ fontSize: '1.5rem' }} />
                                    <div>
                                        <strong>Aprobado</strong>
                                        <p className="mb-0 text-muted small">El siniestro ha sido aprobado por la ART</p>
                                    </div>
                                </div>
                            )}
                            {calificacionSeleccionada === 'RECHAZADO' && (
                                <div className="d-flex align-items-center gap-2">
                                    <CancelIcon className="text-danger" style={{ fontSize: '1.5rem' }} />
                                    <div>
                                        <strong>Rechazado</strong>
                                        <p className="mb-0 text-muted small">El siniestro ha sido rechazado por la ART</p>
                                    </div>
                                </div>
                            )}
                            {calificacionSeleccionada === 'OTRO' && (
                                <div className="d-flex align-items-center gap-2">
                                    <HelpIcon className="text-secondary" style={{ fontSize: '1.5rem' }} />
                                    <div>
                                        <strong>Otro</strong>
                                        <p className="mb-0 text-muted small">Ingrese una etiqueta personalizada</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {calificacionSeleccionada === 'OTRO' && (
                        <div className="mb-3">
                            <label className="form-label fw-semibold">Etiqueta Personalizada</label>
                            <Form.Control
                                type="text"
                                placeholder="Ej: En Revisión, Pendiente, etc."
                                value={etiquetaOtra}
                                onChange={(e) => setEtiquetaOtra(e.target.value)}
                                className="rounded-3"
                            />
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Comentario (Obligatorio)</label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Ingrese un comentario justificando la calificación..."
                            value={comentarioTexto}
                            onChange={(e) => setComentarioTexto(e.target.value)}
                            className="rounded-3"
                        />
                        <small className="text-muted">Este comentario se guardará en el historial del siniestro.</small>
                    </div>
                </Modal.Body>
                <Modal.Footer className="px-4 py-3">
                    <Button variant="secondary" onClick={cerrarModalCalificacion} disabled={cargandoCalificacion}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleGuardarCalificacion} disabled={cargandoCalificacion}>
                        {cargandoCalificacion ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            'Guardar Calificación'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ListarSiniestros;
